import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonRouterOutlet, IonTabs, IonTabBar, IonTabButton, IonIcon, IonLabel } from '@ionic/react';
import {
  homeOutline,
  radioOutline,
  barChartOutline,
  settingsOutline,
  mapOutline,
  notificationsOutline,
  sparklesOutline,
  shieldCheckmarkOutline,
  ellipsisHorizontal,
  navigateCircleOutline,
} from 'ionicons/icons';
import { useAuth } from '../../application/use-cases/auth/AuthContext';
import { useIsDesktop } from '../components/useIsDesktop';
import Dashboard from './Dashboard';
import Devices from './Devices';
import DeviceDetail from './DeviceDetail';
import IrrigationSchedule from './IrrigationSchedule';
import StationThresholds from './StationThresholds';
import IrrigationHistory from './IrrigationHistory';
import StationMetricDetail from './StationMetricDetail';
import StationPage from './StationPage';
import Sites from './Sites';
import ClaimHub from './ClaimHub';
import HubDetail from './HubDetail';
import AddStation from './AddStation';
import SiteMap from './SiteMap';
import Lots from './Lots';
import LotDetail from './LotDetail';
import LotForm from './LotForm';
import History from './History';
import Settings from './Settings';
import More from './More';
import Alerts from './Alerts';
import AiConversationList from './AiConversationList';
import AiConversationThread from './AiConversationThread';
import ChangePassword from './ChangePassword';
import AdminRoute from '../components/AdminRoute';
import AdminHome from './admin/AdminHome';
import AdminUsers from './admin/AdminUsers';
import AdminDevices from './admin/AdminDevices';
import AdminAlerts from './admin/AdminAlerts';
import AdminIrrigation from './admin/AdminIrrigation';
import AdminEmails from './admin/AdminEmails';
import AdminAi from './admin/AdminAi';
import AdminDeviceMqtt from './admin/AdminDeviceMqtt';
import AdminHubs from './admin/AdminHubs';
import AdminHubDetail from './admin/AdminHubDetail';
import AdminFirmware from './admin/AdminFirmware';
import AdminLots from './admin/AdminLots';

// En el celular, barra de pestañas flotante abajo: Inicio, Campo (lotes),
// Sensores (estaciones), Historial y Más (mapa, alertas, asistente, ajustes,
// admin). En escritorio la misma barra pasa a ser una barra lateral (ver
// theme/desktop.css) con todo a la vista, sin "Más".
const MainTabs: React.FC = () => {
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  // Siempre explícito: con undefined, React pisa el default de Ionic y el
  // botón queda con la clase tab-layout-undefined (ícono chico, sin texto).
  const layout = isDesktop ? 'icon-start' : 'icon-top';

  return (
  <IonTabs>
    <IonRouterOutlet>
      <Route exact path="/app/dashboard" component={Dashboard} />
      <Route exact path="/app/devices" component={Devices} />
      <Route exact path="/app/devices/:id" component={DeviceDetail} />
      <Route exact path="/app/devices/:id/thresholds" component={StationThresholds} />
      <Route exact path="/app/devices/:id/valves/:channel/irrigation" component={IrrigationSchedule} />
      <Route exact path="/app/devices/:id/valves/:channel/irrigation/history" component={IrrigationHistory} />
      <Route exact path="/app/stations/:id([0-9a-f-]{36})" component={StationPage} />
      <Route exact path="/app/stations/:id/suelo/:channel" component={StationMetricDetail} />
      <Route exact path="/app/stations/:id/:metric" component={StationMetricDetail} />
      <Route exact path="/app/sites" component={Sites} />
      <Route exact path="/app/hubs/vincular" component={ClaimHub} />
      <Route exact path="/app/hubs/:id(hub-[0-9a-f]{6})" component={HubDetail} />
      <Route exact path="/app/estaciones/nueva" component={AddStation} />
      <Route exact path="/app/lots" component={Lots} />
      <Route exact path="/app/lots/new" component={LotForm} />
      {/* :id solo con forma de UUID, así /app/lots/new no cae en el detalle. */}
      <Route exact path="/app/lots/:id([0-9a-f-]{36})/edit" component={LotForm} />
      <Route exact path="/app/lots/:id([0-9a-f-]{36})" component={LotDetail} />
      <Route exact path="/app/map" component={SiteMap} />
      <Route exact path="/app/history" component={History} />
      <Route exact path="/app/settings" component={Settings} />
      <Route exact path="/app/more" component={More} />
      <Route exact path="/app/alerts" component={Alerts} />
      <Route exact path="/app/ai" component={AiConversationList} />
      <Route exact path="/app/ai/:id" component={AiConversationThread} />
      <Route exact path="/app/change-password" component={ChangePassword} />
      <AdminRoute exact path="/app/admin">
        <AdminHome />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/users">
        <AdminUsers />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/devices">
        <AdminDevices />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/devices/:id/mqtt">
        <AdminDeviceMqtt />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/hubs">
        <AdminHubs />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/hubs/:id(hub-[0-9a-f]{6})">
        <AdminHubDetail />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/firmware">
        <AdminFirmware />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/lots">
        <AdminLots />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/lots/:id([0-9a-f-]{36})">
        <LotDetail admin />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/lots/:id([0-9a-f-]{36})/edit">
        <LotForm admin />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/alerts">
        <AdminAlerts />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/irrigation">
        <AdminIrrigation />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/emails">
        <AdminEmails />
      </AdminRoute>
      <AdminRoute exact path="/app/admin/ai">
        <AdminAi />
      </AdminRoute>
      <Route exact path="/app">
        <Redirect to="/app/dashboard" />
      </Route>
    </IonRouterOutlet>
    <IonTabBar slot="bottom">
      <IonTabButton tab="dashboard" href="/app/dashboard" layout={layout}>
        <IonIcon icon={homeOutline} />
        <IonLabel>Inicio</IonLabel>
      </IonTabButton>
      <IonTabButton tab="lots" href="/app/lots" layout={layout}>
        <IonIcon icon={mapOutline} />
        <IonLabel>Campo</IonLabel>
      </IonTabButton>
      <IonTabButton tab="map" href="/app/map" layout={layout} className="desktop-only">
        <IonIcon icon={navigateCircleOutline} />
        <IonLabel>Mapa</IonLabel>
      </IonTabButton>
      <IonTabButton tab="devices" href="/app/devices" layout={layout}>
        <IonIcon icon={radioOutline} />
        <IonLabel>Sensores</IonLabel>
      </IonTabButton>
      <IonTabButton tab="history" href="/app/history" layout={layout}>
        <IonIcon icon={barChartOutline} />
        <IonLabel>Historial</IonLabel>
      </IonTabButton>
      <IonTabButton tab="alerts" href="/app/alerts" layout={layout} className="desktop-only">
        <IonIcon icon={notificationsOutline} />
        <IonLabel>Alertas</IonLabel>
      </IonTabButton>
      <IonTabButton tab="ai" href="/app/ai" layout={layout} className="desktop-only">
        <IonIcon icon={sparklesOutline} />
        <IonLabel>Asistente</IonLabel>
      </IonTabButton>
      <IonTabButton tab="settings" href="/app/settings" layout={layout} className="desktop-only">
        <IonIcon icon={settingsOutline} />
        <IonLabel>Ajustes</IonLabel>
      </IonTabButton>
      {user?.isAdmin && (
        <IonTabButton tab="admin" href="/app/admin" layout={layout} className="desktop-only">
          <IonIcon icon={shieldCheckmarkOutline} />
          <IonLabel>Admin</IonLabel>
        </IonTabButton>
      )}
      <IonTabButton tab="more" href="/app/more" layout={layout} className="mobile-only">
        <IonIcon icon={ellipsisHorizontal} />
        <IonLabel>Más</IonLabel>
      </IonTabButton>
    </IonTabBar>
  </IonTabs>
  );
};

export default MainTabs;
